import { EMPTY_FIELD_PLACEHOLDER } from '@/consts/formatting';
import { AmendmentsService } from '@/services/Amendments.service';
import { PlanService } from '@/services/Plan.service';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Unstable_Grid2 as Grid,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { grey } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import { useQueries, useQuery } from '@tanstack/react-query';

import { useMemo, useState } from 'react';

import { AmendmentStatus } from './CreateNewAmendmentDialog.component';
import { PlanAmendmentDetail } from './PlanAmendmentDetail.component';

interface StatusAccordionProps {
  expanded: boolean;
  onChange: (e: React.SyntheticEvent, isExpanded: boolean) => void;
  status: string;
  updatedFilters: Record<string, any>;
}

export const StatusAccordion: React.FC<StatusAccordionProps> = props => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const theme = useTheme();

  const columns = useMemo(
    () => [
      { id: 'plan', label: 'Plan', width: 350 },
      { id: 'provision', label: 'Provision(s)', width: 400 },
      { id: 'billable', label: 'Billable', width: 250 },
      { id: 'effectiveDate', label: 'Effective Date', width: 125 },
      { id: 'assignee', label: 'Assignee', width: 200 },
      { id: 'subtasks', label: 'Subtasks', width: 30 }
    ],
    []
  );

  const amendments = useQuery(
    ['AmendmentService.getAmendments', props.status, props.updatedFilters],
    () => {
      return AmendmentsService.getAmendments(
        props.status,
        props.updatedFilters.search,
        props.updatedFilters.searchType,
        props.updatedFilters.productTierId,
        props.updatedFilters.billableAmount,
        props.updatedFilters.assignee,
        props.updatedFilters.adminStatus,
        props.updatedFilters.firmId
      );
    },
    { enabled: !!props.status || Boolean(props.updatedFilters) }
  );

  const planIds =
    amendments?.data?.map(amendment => amendment.sponsorPlanId) || [];

  const plans = useQueries({
    queries: planIds?.map(id => ({
      enabled: !!id,
      queryFn: () => PlanService.getPlanById(id),
      queryKey: ['PlanService.getPlanById', id]
    }))
  });

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    id: number
  ) => {
    setAnchorEl(event.currentTarget);
    setAnchorId(id);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAnchorId(null);
  };

  const defaultSubtasks = [
    'Review Amendment',
    'Draft Plan Document Amendment',
    'Review Drafted Plan Document Amendment',
    'Deliver Amendment to Client (cc Advisor)',
    'Receive Executed Document(s) and Update Portal',
    'Send Participant Notices',
    'Amendment Cross Functional Alerting',
    'Final Review of Admin and Executed Document Organization'
  ];

  const subtasks = {
    'Add Loans': defaultSubtasks,
    'Auto-Enroll - add/remove': defaultSubtasks,
    'Company Information': defaultSubtasks,
    'Contributions - Deferral and Safe Harbor': defaultSubtasks,
    'Contributions - Matching or Non-elective': defaultSubtasks,
    'Custom Effective Date and custom language': defaultSubtasks,
    Distributions: defaultSubtasks,
    'Eligiblity and/or Entry Date': defaultSubtasks,
    'General Information': defaultSubtasks,
    'In-Service Withdrawals': defaultSubtasks,
    'Loan Policy Updates': [
      'Review Amendment',
      'Receive Executed Documents and Update Portal'
    ],
    'Participanting Employer - add/remove/edit': defaultSubtasks,
    'TPA Submission': [
      'Review Amendment',
      'Receive Executed Documents and Update Portal',
      'Cross Functional Alerting'
    ],
    Vesting: defaultSubtasks,
    'Vesting Override': ['Review Amendment', 'Cross Functional Alerting']
  };

  const amendmentsFromLast30Days =
    amendments.data?.filter(a => {
      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      if (
        [
          AmendmentStatus.COMPLETED.toString(),
          AmendmentStatus.CANCELLED_OR_REJECTED.toString()
        ].includes(a.status) &&
        new Date(a.createdAt) >= thirtyDaysAgo &&
        new Date(a.createdAt) <= now
      )
        return a;
    }) || [];

  const makeProvisionCell = (provisions: string[], sponsorPlanId: number) => {
    const cache = {};
    provisions.forEach(
      provision => (cache[provision] = provision.split('').length)
    );
    let total = 0;

    for (const [key, value] of Object.entries(cache)) {
      total += +value;
      if (total > 45) {
        const exceededIndex = provisions.indexOf(key);
        const includedProvisions = provisions.slice(0, exceededIndex);
        return {
          hasExceeded: true,
          hiddenTiles: provisions.slice(exceededIndex),
          sponsorPlanId,
          tiles: includedProvisions
        };
      }
    }
    return {
      hasExceeded: false,
      tiles: provisions
    };
  };

  return (
    <Accordion
      aria-label={`${props.status} amendments`}
      data-testid={`status-accordion-${props.status}`}
      elevation={0}
      expanded={props.expanded}
      onChange={props.onChange}
      sx={{ width: '100%' }}>
      <AccordionSummary
        aria-controls={`${props.status.toLowerCase().replace(/\s+/g, '-')}-panel-content`}
        expandIcon={props.expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
        id='panel-header'
        sx={{
          backgroundColor: [
            AmendmentStatus.COMPLETED.toString(),
            AmendmentStatus.CANCELLED_OR_REJECTED.toString()
          ].includes(props.status)
            ? theme.palette.success.lightBg
            : grey[100],
          border: `1px ${grey[300]} solid`,
          borderRadius: '4px',
          elevation: 0
        }}>
        <Grid
          columnSpacing={1}
          container
          justifyContent='space-between'
          xs={12}>
          <Grid container xs={8}>
            <Grid>
              <Typography flexDirection='row' fontSize={12} fontWeight='bold'>
                {props.status}
              </Typography>
            </Grid>
            <Grid>
              <Divider
                orientation='vertical'
                sx={{ margin: 0 }}
                variant='middle'
              />
            </Grid>
            <Grid>
              {[
                AmendmentStatus.COMPLETED.toString(),
                AmendmentStatus.CANCELLED_OR_REJECTED.toString()
              ].includes(props.status) ? (
                <Typography fontSize={12}>
                  &nbsp;
                  {`${amendmentsFromLast30Days.length} tickets in the last 30 days`}{' '}
                </Typography>
              ) : (
                <Typography fontSize={12}>
                  &nbsp;{`${amendments.data?.length || 0} tickets`}{' '}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid paddingRight={1}>
            <Typography fontSize={12}>
              {props.expanded ? 'Collapse' : 'Expand'}
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          border: 1,
          borderColor: grey[300],
          borderRadius: '4px',
          padding: 0
        }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    align='left'
                    key={column.id}
                    sx={{
                      borderRight:
                        column.label === 'Plan' ? '1px solid #ccc' : 0,
                      minWidth: column.width - 50,
                      width: column.width
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {amendments.isLoading && (
                <TableRow>
                  <TableCell align='center' colSpan={columns.length}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {amendments.isError && (
                <TableRow>
                  <TableCell align='center' colSpan={columns.length}>
                    <Typography color='error'>
                      Error loading amendments
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!amendments.isLoading &&
                !amendments.isError &&
                amendments.data?.length === 0 && (
                  <TableRow>
                    <TableCell align='center' colSpan={columns.length}>
                      <Typography>No amendments found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              {plans.map(plan => {
                return amendments.data
                  ?.filter(
                    amendment => amendment.sponsorPlanId === plan.data?.data?.id
                  )
                  .map(amendment => {
                    const isBillable =
                      amendment.billing.split('-')[0] !== 'Waived ';

                    const subtaskList = amendment.provisions.flatMap(
                      provision => subtasks[provision]
                    );

                    const provisionsCell = makeProvisionCell(
                      amendment.provisions,
                      amendment.sponsorPlanId
                    );

                    const desiredEffectiveDate = new Date(
                      amendment.desiredEffectiveDate
                    );
                    const now = new Date();
                    const differenceInMinutes =
                      desiredEffectiveDate.getTime() - now.getTime();
                    const differenceInDays = Math.round(
                      differenceInMinutes / (1000 * 60 * 60 * 24)
                    );

                    return (
                      <TableRow key={plan?.data?.data?.id}>
                        <TableCell
                          sx={{ borderRight: '1px solid #ccc', padding: 0 }}>
                          <PlanAmendmentDetail
                            amendmentId={amendment.id}
                            isInteractive
                            isJPM={
                              plan.data?.data?.relationships?.firm?.data?.id ===
                              1245
                            }
                            isOnboarding={
                              plan.data?.data?.attributes?.status ===
                              'Onboarding'
                            }
                            planName={plan?.data?.data?.attributes?.name}
                            size='small'
                            sponsorPlanId={plan?.data?.data?.id}
                            status={amendment.status}
                            updatedFilters={props.updatedFilters}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}>
                          <Grid container spacing={1}>
                            {provisionsCell.tiles.map(provision => (
                              <Grid key={amendment.id}>
                                <Box
                                  sx={{
                                    backgroundColor: theme.palette.grey[100],
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    borderRadius: 1,
                                    paddingX: 1
                                  }}>
                                  {provision}
                                </Box>
                              </Grid>
                            ))}
                            {provisionsCell.hasExceeded && (
                              <Grid paddingTop={0.75}>
                                <Typography
                                  aria-haspopup='true'
                                  aria-owns={
                                    open ? 'mouse-over-popover' : undefined
                                  }
                                  data-testid='hover-anchor-el'
                                  fontSize={13}
                                  onMouseEnter={e =>
                                    handlePopoverOpen(e, amendment.id)
                                  }
                                  onMouseLeave={handlePopoverClose}>
                                  {`+ ${provisionsCell.hiddenTiles.length}`}
                                </Typography>
                                <Popover
                                  anchorEl={anchorEl}
                                  anchorOrigin={{
                                    horizontal: 'left',
                                    vertical: 'bottom'
                                  }}
                                  data-testid='mouse-over-popover'
                                  disableRestoreFocus
                                  id={`mouse-over-popover-${amendment.id}`}
                                  onClose={handlePopoverClose}
                                  open={anchorId === amendment.id}
                                  sx={{ pointerEvents: 'none' }}
                                  transformOrigin={{
                                    horizontal: 'left',
                                    vertical: 'top'
                                  }}>
                                  <Grid padding={1}>
                                    {provisionsCell.sponsorPlanId ===
                                      amendment.sponsorPlanId &&
                                      provisionsCell.hiddenTiles.map(
                                        (provision, index) => (
                                          <Typography fontSize={12} key={index}>
                                            {provision}
                                          </Typography>
                                        )
                                      )}
                                  </Grid>
                                </Popover>
                              </Grid>
                            )}
                          </Grid>
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                          {isBillable ? (
                            <>
                              <Grid columnSpacing={0.5} container>
                                <Grid>
                                  <Typography
                                    color={theme.palette.success.light}>
                                    Yes
                                  </Typography>
                                </Grid>
                                <Grid>
                                  <CheckIcon
                                    data-testid='check-icon'
                                    fontSize='small'
                                    sx={{
                                      color: theme.palette.success.light
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Typography
                                color={theme.palette.grey[600]}
                                fontSize={12}>
                                {amendment.billing}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Grid columnSpacing={0.5} container>
                                <Grid>
                                  <Typography color={theme.palette.error.light}>
                                    No
                                  </Typography>
                                </Grid>
                                <Grid paddingTop={0.3}>
                                  <CloseIcon
                                    data-testid='close-icon'
                                    fontSize='small'
                                    sx={{
                                      color: theme.palette.error.light
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Typography
                                color={theme.palette.grey[600]}
                                fontSize={12}>
                                {amendment.billing}
                              </Typography>
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography key={amendment.id}>
                            {new Date(
                              amendment.desiredEffectiveDate
                            ).toLocaleDateString()}
                          </Typography>
                          <Typography
                            color={theme.palette.grey[600]}
                            fontSize={12}>
                            {amendment.desiredEffectiveDate >
                            new Date().toLocaleDateString()
                              ? `in ${differenceInDays} days`
                              : ''}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {amendment.assignee || EMPTY_FIELD_PLACEHOLDER}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ flexDirection: 'row' }}>
                            {`0 / ${[...new Set(subtaskList)].length}`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};
